/**
 * StudioNotifications — Notification Center (per-user inbox).
 *
 * History, mark-read, snooze, archive, action buttons. Real-time updates
 * via the WS feed (notif_created event). Plus per-user settings panel.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Bell, ArrowLeft, Check, Archive, Clock, Settings, AlertTriangle, Loader2,
  Sparkles, Filter, ChevronRight, Inbox,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useStudioFeed from "./useStudioFeed";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

const PRIORITY_COLORS = {
  critical: "#ef4444",
  high: "#f59e0b",
  normal: "#a78bfa",
  low: "#64748b",
};

export default function StudioNotifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState({ unread: false, kind: "" });
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { events } = useStudioFeed();

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.unread) params.set("only_unread", "true");
      if (filter.kind) params.set("filter_kind", filter.kind);
      if (showArchived) params.set("archived", "true");
      const r = await axios.get(`${API}/api/studio/notif/inbox?${params}`, { withCredentials: true });
      setItems(r.data?.items || []);
      setUnread(r.data?.unread || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  // Initial + filter changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter.unread, filter.kind, showArchived]);

  // Realtime: when a notif_created event fires, refresh
  useEffect(() => {
    if (events.length && events[0]?.type === "notif_created") {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  const markRead = async (id) => {
    await axios.post(`${API}/api/studio/notif/${id}/read`, {}, { withCredentials: true });
    setItems((x) => x.map((n) => n._id === id ? { ...n, read: true } : n));
    setUnread((u) => Math.max(0, u - 1));
  };
  const markAllRead = async () => {
    await axios.post(`${API}/api/studio/notif/read-all`, {}, { withCredentials: true });
    setItems((x) => x.map((n) => ({ ...n, read: true })));
    setUnread(0);
    toast.success("All marked read");
  };
  const snooze = async (id, minutes) => {
    await axios.post(`${API}/api/studio/notif/${id}/snooze`, { minutes }, { withCredentials: true });
    setItems((x) => x.filter((n) => n._id !== id));
    toast.success(`Snoozed for ${minutes}m`);
  };
  const archive = async (id) => {
    await axios.post(`${API}/api/studio/notif/${id}/archive`, {}, { withCredentials: true });
    setItems((x) => x.filter((n) => n._id !== id));
    toast.success("Archived");
  };

  return (
    <>
      <Header />
      <div className="studio-shell">
        <div className="studio-hero" style={{ paddingBottom: 16 }}>
          <Link to="/studio" className="studio-btn-ghost" data-testid="notif-back" style={{ marginBottom: 12 }}>
            <ArrowLeft size={14} /> Back to Studio
          </Link>
          <span className="studio-eyebrow">
            <Bell size={12} /> Notification Center
          </span>
          <h1 className="studio-h1">
            All your alerts, <span className="studio-h1-accent">one inbox.</span>
          </h1>
          <p className="studio-sub">
            Morning briefs, escalations, evening reports — everything routed by the auto-scheduler. Real-time push via WebSocket.
          </p>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            <button
              className={filter.unread ? "studio-btn-primary" : "studio-btn-ghost"}
              onClick={() => setFilter((f) => ({ ...f, unread: !f.unread }))}
              data-testid="notif-filter-unread"
            >
              <Filter size={14} /> Unread only {unread > 0 && <span className="studio-pill studio-pill--sm" style={{ marginLeft: 6 }}>{unread}</span>}
            </button>
            <select
              className="studio-input"
              value={filter.kind}
              onChange={(e) => setFilter((f) => ({ ...f, kind: e.target.value }))}
              data-testid="notif-filter-kind"
              style={{ maxWidth: 200 }}
            >
              <option value="">All kinds</option>
              <option value="morning_reminder">Morning brief</option>
              <option value="evening_report">Evening report</option>
              <option value="escalation_reminder">Escalation · 2h</option>
              <option value="escalation_warning">Escalation · 4h</option>
              <option value="escalation_overdue">Escalation · overdue</option>
              <option value="escalation_critical">Escalation · critical</option>
            </select>
            <button
              className={showArchived ? "studio-btn-primary" : "studio-btn-ghost"}
              onClick={() => setShowArchived((v) => !v)}
              data-testid="notif-toggle-archived"
            >
              <Archive size={14} /> {showArchived ? "Hide archived" : "Show archived"}
            </button>
            <button className="studio-btn-ghost" onClick={markAllRead} disabled={!unread} data-testid="notif-mark-all-read">
              <Check size={14} /> Mark all read
            </button>
            <button className="studio-btn-ghost" onClick={() => setSettingsOpen(true)} data-testid="notif-settings-btn">
              <Settings size={14} /> Settings
            </button>
          </div>
        </div>

        {loading && (
          <div className="studio-skeleton studio-sk-card" style={{ height: 100 }} />
        )}

        {!loading && !items.length && (
          <div className="studio-empty" data-testid="notif-empty">
            <div className="studio-empty-icon"><Inbox size={36} /></div>
            <h2>Inbox zero</h2>
            <p>Nothing to attend to right now. Auto-scheduler will route alerts here as they appear.</p>
          </div>
        )}

        <div style={{ display: "grid", gap: 10 }}>
          {items.map((n, i) => (
            <motion.div
              key={n._id}
              className="studio-card"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.02 }}
              style={{
                padding: 16,
                borderLeft: `3px solid ${PRIORITY_COLORS[n.priority] || PRIORITY_COLORS.normal}`,
                opacity: n.read ? 0.78 : 1,
              }}
              data-testid={`notif-item-${n._id}`}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {!n.read && <span style={{ width: 8, height: 8, borderRadius: "50%", background: PRIORITY_COLORS[n.priority] || PRIORITY_COLORS.normal }} />}
                    <strong style={{ fontSize: 14 }}>{n.title}</strong>
                    <span className="studio-pill studio-pill--sm" style={{ textTransform: "uppercase", fontSize: 10 }}>
                      {n.priority}
                    </span>
                    <small className="studio-muted" style={{ marginLeft: "auto" }}>
                      {new Date(n.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </small>
                  </div>
                  <pre style={{ margin: "8px 0 0", fontSize: 13, color: "var(--st-text-soft)", whiteSpace: "pre-wrap", fontFamily: "inherit", lineHeight: 1.55 }}>
                    {n.message}
                  </pre>
                  <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                    {n.action_url && (
                      <Link to={n.action_url} className="studio-btn-primary" style={{ padding: "5px 12px", fontSize: 12 }} data-testid={`notif-action-${n._id}`}>
                        <ChevronRight size={11} /> {n.action_label || "Open"}
                      </Link>
                    )}
                    {!n.read && (
                      <button className="studio-btn-ghost" onClick={() => markRead(n._id)} data-testid={`notif-read-${n._id}`} style={{ padding: "5px 12px", fontSize: 12 }}>
                        <Check size={11} /> Read
                      </button>
                    )}
                    <button className="studio-btn-ghost" onClick={() => snooze(n._id, 60)} data-testid={`notif-snooze-${n._id}`} style={{ padding: "5px 12px", fontSize: 12 }}>
                      <Clock size={11} /> Snooze 1h
                    </button>
                    <button className="studio-btn-ghost" onClick={() => archive(n._id)} data-testid={`notif-archive-${n._id}`} style={{ padding: "5px 12px", fontSize: 12 }}>
                      <Archive size={11} /> Archive
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
      <Footer />
    </>
  );
}

function SettingsModal({ onClose }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/studio/notif/settings`, { withCredentials: true })
      .then((r) => setS(r.data))
      .catch(() => toast.error("Failed to load settings"));
  }, []);
  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/studio/notif/settings`, {
        morning_disabled: s.morning_disabled,
        only_when_action_needed: s.only_when_action_needed,
        email_enabled: s.email_enabled,
        wa_enabled: s.wa_enabled,
        phone: s.phone,
        timezone_offset_hours: Number(s.timezone_offset_hours),
        reminder_hour_local: Number(s.reminder_hour_local),
      }, { withCredentials: true });
      toast.success("Saved");
      onClose();
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };
  const sendTestWa = async () => {
    if (!s?.phone) { toast.error("Add phone first"); return; }
    try {
      const r = await axios.post(`${API}/api/studio/notif/admin/test-wa`,
        { phone: s.phone, message: "✅ Test from MarkeeMix Studio · your WhatsApp is connected." },
        { withCredentials: true },
      );
      if (r.data?.ok) toast.success("WhatsApp test sent");
      else toast.error("WhatsApp test failed — see backend logs");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "WhatsApp test failed");
    }
  };
  if (!s) return null;
  return (
    <div className="studio-vid-overlay" onClick={onClose}>
      <div
        className="studio-vid-modal"
        role="dialog"
        data-testid="notif-settings-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520 }}
      >
        <header><h3>Notification Settings</h3><button className="studio-btn-ghost" onClick={onClose}>×</button></header>
        <div style={{ padding: 18, display: "grid", gap: 14 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="checkbox"
              checked={!s.morning_disabled}
              onChange={(e) => setS({ ...s, morning_disabled: !e.target.checked })}
              data-testid="notif-pref-morning-toggle"
            />
            <span>Daily 9 AM morning brief</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="checkbox"
              checked={!!s.only_when_action_needed}
              onChange={(e) => setS({ ...s, only_when_action_needed: e.target.checked })}
              data-testid="notif-pref-only-action"
            />
            <span>Only when there is something to do</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="checkbox"
              checked={!!s.email_enabled}
              onChange={(e) => setS({ ...s, email_enabled: e.target.checked })}
              data-testid="notif-pref-email"
            />
            <span>Send copy to email</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="checkbox"
              checked={!!s.wa_enabled}
              onChange={(e) => setS({ ...s, wa_enabled: e.target.checked })}
              data-testid="notif-pref-wa"
            />
            <span>Send copy to WhatsApp (Nextel)</span>
          </label>
          <div>
            <small className="studio-muted">WhatsApp number (with country code, e.g. +9198XXXXXXXX)</small>
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              <input
                className="studio-input"
                placeholder="+9198XXXXXXXX"
                value={s.phone || ""}
                onChange={(e) => setS({ ...s, phone: e.target.value })}
                data-testid="notif-pref-phone"
                style={{ flex: 1 }}
              />
              <button
                className="studio-btn-ghost"
                onClick={sendTestWa}
                disabled={!s.phone}
                data-testid="notif-pref-wa-test"
                style={{ padding: "8px 14px" }}
              >
                Send test
              </button>
            </div>
          </div>
          <div>
            <small className="studio-muted">Reminder hour (local 0-23)</small>
            <input
              className="studio-input"
              type="number" min={0} max={23}
              value={s.reminder_hour_local}
              onChange={(e) => setS({ ...s, reminder_hour_local: e.target.value })}
              data-testid="notif-pref-hour"
            />
          </div>
          <div>
            <small className="studio-muted">Timezone offset hours (e.g. +5 for IST=+5.5 use 5)</small>
            <input
              className="studio-input"
              type="number" min={-12} max={14}
              value={s.timezone_offset_hours}
              onChange={(e) => setS({ ...s, timezone_offset_hours: e.target.value })}
              data-testid="notif-pref-tz"
            />
          </div>
          <button className="studio-btn-primary" onClick={save} disabled={saving} data-testid="notif-pref-save">
            {saving ? <Loader2 size={14} className="ac-spin" /> : <Check size={14} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
